import React, { useState, useMemo, useCallback } from 'react';
import { Column, Row, Expander } from '@amzn/meridian';

import { DEFAULT_ATT_PCT } from '../../utils/constants';
import { isoToEpochSec } from './LaborAvailabilityChartModel';
import ConfigPanel from './shared/ConfigPanel';

import './AttendanceOverride.css';

function getDefaultAttForInterval(intervalAttOverrides, idx) {
  if (!intervalAttOverrides || !Array.isArray(intervalAttOverrides)) return DEFAULT_ATT_PCT;
  const entry = intervalAttOverrides[idx];
  if (entry?.attendancePercentage != null) {
    return Math.round(entry.attendancePercentage * 100 * 10) / 10;
  }
  return DEFAULT_ATT_PCT;
}

function computeRosteredGrid(shiftGroups, intervals) {
  return shiftGroups.map((g) => {
    const gStart = isoToEpochSec(g.start);
    const gEnd = isoToEpochSec(g.end);
    return intervals.map((iv) => {
      const dur = iv.endTime - iv.startTime;
      if (dur <= 0) return 0;
      const overlap = Math.max(0, Math.min(gEnd, iv.endTime) - Math.max(gStart, iv.startTime));
      if (overlap <= 0) return 0;
      return g.expectedHeadcount * (overlap / dur);
    });
  });
}

function computeVtoPerInterval(vtoAccepted, intervals) {
  if (!vtoAccepted?.length) return intervals.map(() => 0);
  return intervals.map((iv) => {
    const dur = iv.endTime - iv.startTime;
    if (dur <= 0) return 0;
    return vtoAccepted.reduce((sum, v) => {
      const vStart = isoToEpochSec(v.start);
      const vEnd = isoToEpochSec(v.end);
      const overlap = Math.max(0, Math.min(vEnd, iv.endTime) - Math.max(vStart, iv.startTime));
      return sum + (v.accepted || 0) * (overlap / dur);
    }, 0);
  });
}

const formatCell = (val) => (val === 0 ? '—' : val.toFixed(1));

// Backend-vended STPA attendance forecast, shown as-is. Not every shift group has
// one yet, so a missing value renders as a dash.
const formatAttRate = (pct) => (pct == null ? '—' : `${Math.round(pct * 1000) / 10}%`);

// Light pastel colors for shift-group tags — one per position in the semicolon path.
const TAG_COLORS = [
  { bg: '#e8f0fe', border: '#c5d9f5', color: '#2563a8' },
  { bg: '#ecfdf5', border: '#a7f3d0', color: '#166534' },
  { bg: '#fef3c7', border: '#fde68a', color: '#92400e' },
  { bg: '#f3e8ff', border: '#e9d5ff', color: '#6b21a8' }
];

// Convert "CYCLE_1;PICKSTAGE;SINGLE_CYCLE;SORT" → ["Cycle 1", "Pickstage", "Single Cycle", "Sort"]
const formatCohortLabel = (label) =>
  (label || '').split(';').map((seg) =>
    seg
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );

const AttendanceOverride = ({
  laborAvailability,
  intervals,
  attendanceOverrides,
  onAttendanceChange,
  readOnly = false,
  showAttendanceRate = false,
  joined = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const shiftGroups = useMemo(() => laborAvailability?.shiftGroups || [], [laborAvailability]);
  const vtoAccepted = useMemo(() => laborAvailability?.vtoAccepted || [], [laborAvailability]);
  const rosteredGrid = useMemo(() => computeRosteredGrid(shiftGroups, intervals), [shiftGroups, intervals]);

  const sortedGroupIndices = useMemo(() => {
    const withTotal = shiftGroups.map((_, gIdx) => ({
      idx: gIdx,
      total: rosteredGrid[gIdx]?.reduce((s, v) => s + v, 0) || 0
    }));
    withTotal.sort((a, b) => b.total - a.total);
    return withTotal.map((g) => g.idx);
  }, [shiftGroups, rosteredGrid]);

  const displayedGroupIndices = useMemo(
    () => (showAll ? sortedGroupIndices : sortedGroupIndices.slice(0, 5)),
    [showAll, sortedGroupIndices]
  );

  const totalRostered = useMemo(
    () => intervals.map((_, idx) => rosteredGrid.reduce((sum, row) => sum + row[idx], 0)),
    [rosteredGrid, intervals]
  );

  const vtoPerInterval = useMemo(() => computeVtoPerInterval(vtoAccepted, intervals), [vtoAccepted, intervals]);

  const attPerInterval = useMemo(
    () =>
      intervals.map(
        (_, idx) =>
          attendanceOverrides?.[idx] ?? getDefaultAttForInterval(laborAvailability?.intervalAttendanceOverrides, idx)
      ),
    [intervals, attendanceOverrides, laborAvailability]
  );

  const expectedAvailable = useMemo(
    () =>
      intervals.map((_, idx) =>
        rosteredGrid.reduce((sum, row, gIdx) => {
          const base = shiftGroups[gIdx]?.expectedHeadcount || 0;
          if (base <= 0) return sum;
          const attHC = Math.round(base * (attPerInterval[idx] / 100));
          return sum + row[idx] * (attHC / base);
        }, 0)
      ),
    [rosteredGrid, intervals, attPerInterval, shiftGroups]
  );

  const handleAttChange = useCallback(
    (idx, val) => {
      const num = parseFloat(val);
      if (!isNaN(num) && num >= 0 && num <= 100) {
        onAttendanceChange(idx, num);
      }
    },
    [onAttendanceChange]
  );

  if (!shiftGroups.length || !intervals.length) return null;

  return (
    <ConfigPanel joined={joined} className="al-config">
      <Row widths={['fill', 'fit']} alignmentVertical="center">
        <Column spacing="300" alignmentHorizontal="stretch" width="100%">
          <Expander open={isExpanded} onChange={setIsExpanded} title="Available Labor" type="inline" spacingInset="300">
            {sortedGroupIndices.length > 5 && (
              <div className="al-toolbar">
                <label className="al-show-all">
                  <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                  Show All Shift Groups
                </label>
              </div>
            )}

            <div style={{ overflowX: 'auto', width: '100%' }}>
              <table className="al-table">
                <thead>
                  <tr>
                    <th className="al-th al-th-group">Shift Group</th>
                    {showAttendanceRate && <th className="al-th al-th-att-rate">Attendance Rate</th>}
                    {intervals.map((iv, idx) => (
                      <th key={idx} className="al-th al-th-interval">
                        <span className="al-slot-line">
                          <span className="al-slot-primary">{iv.name || `P${idx + 1}`}</span>
                          <span className="al-slot-secondary">
                            {iv.startLabel}–{iv.endLabel}
                          </span>
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Shift group rows */}
                  {displayedGroupIndices.map((gIdx) => (
                    <tr key={gIdx} className="al-row">
                      <td className="al-cell al-cell-group">
                        <div className="al-group-tags">
                          {formatCohortLabel(shiftGroups[gIdx].cohortLabel).map((seg, i) => {
                            const c = TAG_COLORS[i % TAG_COLORS.length];
                            return (
                              <span
                                key={i}
                                className="al-tag"
                                style={{ background: c.bg, borderColor: c.border, color: c.color }}>
                                {seg}
                              </span>
                            );
                          })}
                        </div>
                        <div className="al-group-time">
                          {shiftGroups[gIdx].startLabel}–{shiftGroups[gIdx].endLabel}
                        </div>
                      </td>
                      {showAttendanceRate && (
                        <td className="al-cell al-cell-att-rate">
                          {formatAttRate(shiftGroups[gIdx].attendancePercentage)}
                        </td>
                      )}
                      {intervals.map((_, iIdx) => (
                        <td
                          key={iIdx}
                          className={`al-cell al-cell-value${rosteredGrid[gIdx][iIdx] === 0 ? ' is-zero' : ''}`}>
                          {formatCell(rosteredGrid[gIdx][iIdx])}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Rostered HC total */}
                  <tr className="al-summary-row">
                    <td className="al-cell">
                      <span className="al-summary-label">Rostered HC</span>
                    </td>
                    {showAttendanceRate && <td className="al-cell" />}
                    {totalRostered.map((val, idx) => (
                      <td key={idx} className={`al-cell al-cell-value${val === 0 ? ' is-zero' : ''}`}>
                        {formatCell(val)}
                      </td>
                    ))}
                  </tr>

                  {/* Attendance % (editable) */}
                  <tr className="al-att-row">
                    <td className="al-cell">
                      <span className="al-att-label">Attendance %</span>
                    </td>
                    {showAttendanceRate && <td className="al-cell" />}
                    {intervals.map((iv, idx) => (
                      <td key={idx} className="al-cell" style={{ textAlign: 'center' }}>
                        {totalRostered[idx] > 0 ? (
                          readOnly ? (
                            <span className="al-att-readonly">{attPerInterval[idx]}%</span>
                          ) : (
                            <span className="fp-input al-att-input">
                              <input
                                type="number"
                                value={attPerInterval[idx]}
                                onChange={(e) => handleAttChange(idx, e.target.value)}
                                aria-label={`Attendance % for ${iv.name || `P${idx + 1}`}`}
                              />
                            </span>
                          )
                        ) : (
                          <span className="al-cell-value is-zero">—</span>
                        )}
                      </td>
                    ))}
                  </tr>

                  {/* Scheduled HC */}
                  <tr className="al-summary-row">
                    <td className="al-cell">
                      <span className="al-summary-label">Scheduled HC</span>
                    </td>
                    {showAttendanceRate && <td className="al-cell" />}
                    {expectedAvailable.map((val, idx) => (
                      <td key={idx} className={`al-cell al-cell-value${val === 0 ? ' is-zero' : ''}`}>
                        {formatCell(val)}
                      </td>
                    ))}
                  </tr>

                  {/* VTO Accepted */}
                  <tr className="al-row">
                    <td className="al-cell">
                      <span className="al-summary-label">VTO Accepted</span>
                    </td>
                    {showAttendanceRate && <td className="al-cell" />}
                    {vtoPerInterval.map((val, idx) => (
                      <td key={idx} className="al-cell" style={{ textAlign: 'center' }}>
                        <span className="al-vto-value">{val === 0 ? '—' : `-${val.toFixed(1)}`}</span>
                      </td>
                    ))}
                  </tr>

                  {/* Net Available HC */}
                  <tr className="al-net-row">
                    <td className="al-cell">
                      <span className="al-net-label">Net Available HC</span>
                    </td>
                    {showAttendanceRate && <td className="al-cell" />}
                    {expectedAvailable.map((val, idx) => {
                      const net = Math.max(0, val - vtoPerInterval[idx]);
                      return (
                        <td key={idx} className="al-cell" style={{ textAlign: 'center' }}>
                          <span className={`al-net-value${net === 0 ? ' is-zero' : ''}`}>{formatCell(net)}</span>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </Expander>
        </Column>
      </Row>
    </ConfigPanel>
  );
};

export default AttendanceOverride;
